import { Fragment, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Megaphone, X, Share2, Search, Sparkles, Crown } from "lucide-react";
import { toast } from "sonner";
import { ListingBanner } from "@/components/ListingBanner";
import { getResults, getCategories, getLiveUpdates, getAnnouncements, refreshLive, getGuessing, getPromotions, promoImageUrl, getPassBoard } from "@/services/api";
import { PassBoard } from "@/components/PassBoard";
import { useSettings } from "@/context/SettingsContext";
import { LiveTicker } from "@/components/LiveTicker";
import { useUnlockSync } from "@/components/UnlockGuess";
import { MarketRow, isMarketActive } from "@/components/MarketRow";
import { SectionBanner } from "@/components/SectionBanner";
import { istNow, revealedValue, timeToMinutes, shareResult, shareResults, todayISO } from "@/utils/format";
import logo from "@/assets/bgrmatka-logo.png";

function PromoItem({ p }) {
  const inner = p.image_path ? (
    <img src={promoImageUrl(p.image_path)} alt={p.text || "promo"} className="max-h-20 w-auto max-w-[170px] rounded-lg object-contain shadow-md" />
  ) : (
    <span className="inline-block rounded-full border border-[color:var(--border-gold)] bg-[color:var(--bg-card)] px-3 py-1.5 text-xs font-bold text-[color:var(--accent-gold-bright)] shadow-sm">{p.text}</span>
  );
  if (p.link) {
    return (
      <a href={p.link} target="_blank" rel="noreferrer" data-testid={`promo-${p.id}`} className="inline-block transition-transform hover:scale-105">{inner}</a>
    );
  }
  return <div data-testid={`promo-${p.id}`} className="inline-block">{inner}</div>;
}

export default function Home() {
  const { settings } = useSettings();
  const [categories, setCategories] = useState([]);
  const [latest, setLatest] = useState(null);
  const [live, setLive] = useState([]);
  const [anns, setAnns] = useState([]);
  const [guessing, setGuessing] = useState({});
  const [promos, setPromos] = useState([]);
  const [passBoard, setPassBoard] = useState(null);
  const passedToday = useMemo(() => new Set((passBoard?.date === todayISO() ? passBoard?.items || [] : []).map((p) => p.category_id)), [passBoard]);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [marketQuery, setMarketQuery] = useState("");
  const [tick, setTick] = useState(0);

  const leftPromos = useMemo(() => promos.filter((p) => p.side !== "right"), [promos]);
  const rightPromos = useMemo(() => promos.filter((p) => p.side === "right"), [promos]);

  const [prevDay, setPrevDay] = useState({});
  const prevCache = useRef({ date: "", data: null });
  const loadResults = useCallback(async () => {
    const today = todayISO();
    const y = new Date(`${today}T12:00:00`);
    y.setDate(y.getDate() - 1);
    const yday = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
    const [d, p] = await Promise.all([
      getResults({ date: today, limit: 200 }),
      prevCache.current.date === yday ? Promise.resolve(prevCache.current.data) : getResults({ date: yday, limit: 200 }),
    ]);
    prevCache.current = { date: yday, data: p };
    const prev = {};
    (p.items || []).forEach((r) => {
      if (r.open_panna) prev[r.category_id] = `${r.open_panna}-${r.jodi || "**"}-${r.close_panna || "***"}`;
    });
    const byCat = {};
    (d.items || []).forEach((r) => {
      if (r.result_date !== today) return;
      byCat[r.category_id] = r;
    });
    setLatest(byCat);
    setPrevDay(prev);
  }, []);

  const loadGuessing = useCallback(() => getGuessing().then((d) => {
    const map = {};
    (d.items || []).forEach((g) => { map[g.category_id] = g; });
    setGuessing(map);
  }).catch(() => {}), []);
  useUnlockSync(loadGuessing);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    loadResults().catch(() => setLatest({}));
    getLiveUpdates().then((d) => setLive(d.items || [])).catch(() => {});
    getAnnouncements().then(setAnns).catch(() => {});
    loadGuessing();
    getPromotions().then(setPromos).catch(() => {});
    getPassBoard().then(setPassBoard).catch(() => {});
  }, [loadResults, loadGuessing]);

  useEffect(() => {
    const id = setInterval(() => getPassBoard().then(setPassBoard).catch(() => {}), 30000);
    return () => clearInterval(id);
  }, []);

  // Re-evaluate reveal/countdown every second so results flip and the timer ticks.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Live: pull fresh results every 5s so the source's 5-second updates show up quickly.
  useEffect(() => {
    const id = setInterval(() => {
      loadResults().catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [loadResults]);

  // Slower refresh for things that rarely change (timings, live-update strip).
  useEffect(() => {
    const id = setInterval(() => {
      getCategories().then(setCategories).catch(() => {});
      getLiveUpdates().then((d) => setLive(d.items || [])).catch(() => {});
    }, 45000);
    return () => clearInterval(id);
  }, []);

  // Unified list: ALL markets (own + public). Holiday (weekly-off) markets stay in the
  // list but show "HOLIDAY" instead of a result. Missing results get a masked placeholder.
  const allMarkets = useMemo(() => {
    return categories.map((c) => {
      const r = latest && latest[c.id];
      if (r) return { ...r, open_time: c.open_time, close_time: c.close_time, is_own: !!c.is_own, off_days: c.off_days || [], listing_tier: c.listing_tier || "", prev: prevDay[c.id] };
      return {
        id: `ph-${c.id}`, category_id: c.id, category_name: c.name,
        open_time: c.open_time, close_time: c.close_time, result_date: null,
        open_panna: "", jodi: "", close_panna: "", is_own: !!c.is_own, off_days: c.off_days || [], listing_tier: c.listing_tier || "",
        prev: prevDay[c.id],
      };
    });
  }, [categories, latest, prevDay]);

  const todayStr = todayISO();

  const isHoliday = (m) => Array.isArray(m.off_days) && m.off_days.includes(istNow().getDay());

  // Seconds left in the 1-minute linger for a just-declared market (null when not lingering).
  // Uses server declared_at (advances only when the value actually changes) so it is reload-safe.
  const lingerLeft = (m) => {
    if (!m.declared_at || !(m.result_date === todayStr && m.open_panna)) return null;
    const left = Math.ceil((60000 - (Date.now() - new Date(m.declared_at).getTime())) / 1000);
    return left > 0 && left <= 60 ? left : null;
  };

  // Phase: coming-soon from 5 min before open; once open/close result appears it stays
  // 1 more minute (declared_at) then "settles" and drops to the public list. Holidays never come here.
  const phaseOf = (m) => {
    if (isHoliday(m)) return { comingSoon: false };
    const openM = timeToMinutes(m.open_time);
    const closeM = timeToMinutes(m.close_time);
    const now = istNow();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const hasResult = m.result_date === todayStr && !!m.open_panna;
    const declaredMs = m.declared_at ? new Date(m.declared_at).getTime() : 0;
    const settled = hasResult && Date.now() - declaredMs >= 60000;
    const overnight = openM != null && closeM != null && closeM < openM;
    const windowOpen = openM != null && nowMin >= openM - 5;
    const withinUpper = overnight ? true : closeM == null || nowMin <= closeM + 90;
    const comingSoon = windowOpen && !settled && withinUpper;
    return { comingSoon };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const aajKaResult = useMemo(() => allMarkets.filter((m) => phaseOf(m).comingSoon), [allMarkets, tick]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const featured = useMemo(() => allMarkets.filter((m) => m.listing_tier === "gold"), [allMarkets]);
  const publicList = allMarkets;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liveUpdateCards = useMemo(() => allMarkets.filter((m) => revealedValue(m) !== "***-**-***").slice(0, 8), [allMarkets, tick]);
  // Ticker must respect the SAME reveal gating as the cards/chart (never leak the full
  // result before close). Rebuild each live-update item's value from the gated market data.
  const tickerItems = useMemo(() => {
    const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
    return live.map((it) => {
      const c = catById[it.category_id];
      const r = latest && latest[it.category_id];
      const value = c && r ? revealedValue({ ...r, open_time: c.open_time, close_time: c.close_time }) : "***-**-***";
      return { ...it, result_value: value };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, categories, latest, tick]);

  const refreshOne = useCallback(async (categoryId) => {
    try {
      const d = await getResults({ category_id: categoryId, sort: "desc", limit: 1 });
      const r = (d.items || [])[0];
      if (r && r.result_date === todayISO()) setLatest((prev) => ({ ...(prev || {}), [categoryId]: r }));
      toast.success("Result refreshed");
    } catch {
      toast.error("Refresh failed");
    }
  }, []);

  // Refresh only the markets currently live (in their open-close window).
  const refreshActive = useCallback(async () => {
    const active = categories.filter((c) => isMarketActive(c.open_time, c.close_time));
    if (!active.length) {
      toast.info("No market is active right now");
      return;
    }
    setRefreshingAll(true);
    try {
      await refreshLive().catch(() => null);
      const updates = await Promise.all(
        active.map((c) =>
          getResults({ category_id: c.id, sort: "desc", limit: 1 })
            .then((d) => [c.id, (d.items || [])[0]])
            .catch(() => [c.id, null])
        )
      );
      setLatest((prev) => {
        const next = { ...(prev || {}) };
        updates.forEach(([id, r]) => { if (r) next[id] = r; });
        return next;
      });
      toast.success(`${active.length} live markets refreshed`);
    } finally {
      setRefreshingAll(false);
    }
  }, [categories]);

  const liveNow = useMemo(() => {
    return categories
      .filter((c) => !(Array.isArray(c.off_days) && c.off_days.includes(istNow().getDay())) && isMarketActive(c.open_time, c.close_time))
      .map((c) => ({
        id: c.id, name: c.name, own: !!c.is_own,
        value: latest && latest[c.id] ? revealedValue(latest[c.id]) : "***-**-***",
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, latest, tick]);

  const upcoming = useMemo(() => {
    if (!categories.length) return [];
    const now = istNow();
    const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const toMin = (s) => {
      if (!s) return null;
      const m = s.match(/(\d{1,2})[:.\-](\d{2})/);
      if (!m) return null;
      let h = +m[1]; const u = s.toUpperCase();
      if (u.includes("PM") && h !== 12) h += 12;
      if (u.includes("AM") && h === 12) h = 0;
      return (h % 24) * 60 + +m[2];
    };
    const out = [];
    categories.filter((c) => c.is_own).forEach((c) => {
      [["open", c.open_time], ["close", c.close_time]].forEach(([kind, t]) => {
        const mins = toMin(t);
        if (mins != null) {
          const secs = mins * 60 - nowSec;
          if (secs >= 0 && secs <= 600) out.push({ id: c.id + kind, name: c.name, kind, secs });
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, tick]);

  const fmtCountdown = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;



  useEffect(() => {
    if (upcoming.length === 0) setPopupDismissed(false);
  }, [upcoming.length]);

  const searchQ = marketQuery.trim().toLowerCase();
  const shownAaj = searchQ ? aajKaResult.filter((r) => (r.category_name || "").toLowerCase().includes(searchQ)) : aajKaResult;
  const shownPublic = searchQ ? publicList.filter((r) => (r.category_name || "").toLowerCase().includes(searchQ)) : publicList;

  return (
    <div>
      {/* Top result-alert popup — appears before an own market result is declared */}
      {upcoming.length > 0 && !popupDismissed && (
        <div data-testid="result-alert-popup" className="sticky top-0 z-50 border-b-2 border-[#7A1020] bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#DC2626] shadow-lg">
          <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2.5">
            <span className="live-pulse inline-block h-3 w-3 shrink-0 rounded-full bg-white" />
            <div className="flex-1 overflow-hidden">
              <p className="text-[11px] font-extrabold uppercase tracking-widest text-white/90">Result Coming Soon</p>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                {upcoming.map((u) => (
                  <span key={u.id} data-testid={`alert-${u.id}`} className="text-sm font-extrabold text-white">
                    {u.name}
                    <span data-testid={`alert-countdown-${u.id}`} className="ml-1 rounded bg-white/20 px-1.5 py-0.5 font-mono text-[11px] font-bold tabular-nums">
                      {u.kind === "open" ? "Open" : "Close"} in {fmtCountdown(u.secs)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
            <button
              data-testid="result-alert-dismiss"
              onClick={() => setPopupDismissed(true)}
              className="shrink-0 rounded-full p-1 text-white/80 hover:bg-white/20 hover:text-white"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <LiveTicker items={tickerItems} />

      {/* Golden Ank strip — dpbank's daily lucky number (auto) */}
      {settings.golden_ank && settings.golden_ank_date === todayISO() && (
        <div data-testid="golden-ank-strip" className="border-b-2 border-[#7A1020] bg-gradient-to-r from-[#B8860B] via-[#F3C24B] to-[#B8860B]">
          <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 px-3 py-2 sm:gap-3">
            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#5A0A14] px-2.5 py-1">
              <Sparkles className="h-3.5 w-3.5 text-[#FFD700]" />
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#FFD700] sm:text-[11px]">Golden Ank</span>
            </span>
            <span className="font-mono text-lg font-extrabold tracking-[0.2em] text-[#20050B] sm:text-2xl">{settings.golden_ank}</span>
            <span className="hidden text-[10px] font-bold uppercase tracking-wider text-[#7A1020] sm:inline">· Today's Lucky Number</span>
          </div>
        </div>
      )}

      {/* Top "Live Now" strip — markets currently in their open-close window */}
      {liveNow.length > 0 && (
        <div data-testid="live-now-strip" className="border-b-2 border-[#7A1020] bg-gradient-to-r from-[#0B7A3B] via-[#0E9B4D] to-[#0B7A3B]">
          <div className="mx-auto flex max-w-6xl items-center gap-3 px-3 py-2">
            <span className="live-fx flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1">
              <span className="live-pulse inline-block h-2.5 w-2.5 rounded-full bg-white" />
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-white">Live Now</span>
            </span>
            <div className="flex flex-1 items-center gap-4 overflow-x-auto whitespace-nowrap pb-0.5">
              {liveNow.map((m) => (
                <span key={m.id} data-testid={`live-now-${m.id}`} className="inline-flex items-center gap-1.5">
                  <span className="text-xs font-extrabold uppercase text-white">{m.name}</span>
                  <span className="font-mono text-sm font-extrabold tabular-nums text-[#FFE9A8]">{m.value}</span>
                </span>
              ))}
            </div>
            <button
              data-testid="refresh-all-btn"
              onClick={refreshActive}
              disabled={refreshingAll}
              className="ml-2 inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshingAll ? "animate-spin" : ""}`} /> Refresh All
            </button>
          </div>
        </div>
      )}

      {/* Compact hero with admin-managed promos on both sides */}
      <section className="maroon-gradient border-b border-[color:var(--border-gold)]">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:py-8">
          <div className="flex items-center justify-center gap-4 sm:gap-8">
            <div className="hidden flex-1 flex-col items-end gap-2 md:flex" data-testid="promos-left">
              {leftPromos.map((p) => <PromoItem key={p.id} p={p} />)}
            </div>
            <div className="shrink-0 text-center">
              <img src={logo} alt={settings.business_name} data-testid="hero-logo" className="mx-auto h-24 w-auto object-contain drop-shadow-lg sm:h-28" />
              <div><span data-testid="hero-premium-ring" className="btn-ring premium-blink mt-2 p-[3px]">
              <Link to="/premium-guessing" data-testid="hero-premium-link"
                className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 px-4 py-1.5 text-sm font-black uppercase tracking-wide text-[#3b1d00] shadow-md">
                <Crown className="h-4 w-4" /> Premium Membership
              </Link>
              </span></div>
              <h1 className="sr-only">{settings.business_name}</h1>
              <p className="mt-2 text-sm text-[color:var(--text-secondary)]">{settings.tagline}</p>
            </div>
            <div className="hidden flex-1 flex-col items-start gap-2 md:flex" data-testid="promos-right">
              {rightPromos.map((p) => <PromoItem key={p.id} p={p} />)}
            </div>
          </div>
          {(leftPromos.length > 0 || rightPromos.length > 0) && (
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 md:hidden" data-testid="promos-mobile">
              {[...leftPromos, ...rightPromos].map((p) => <PromoItem key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>

      {/* Lucky Number / Final Ank strip (editable from Admin) */}
      {(settings.lucky_ank || settings.final_ank) && (
        <div data-testid="lucky-ank-strip" className="border-b-2 border-[#7A1020] bg-gradient-to-r from-[#F3C24B] via-[#FFD700] to-[#F3C24B]">
          <div className="mx-auto max-w-6xl px-3 py-2">
            <p className="text-center text-[11px] font-extrabold uppercase tracking-widest text-[#5A0A14]">
              Today {settings.business_name} Lucky Number
            </p>
            <div className="mt-1 grid grid-cols-2 divide-x divide-[#7A1020]/40">
              <div className="px-2 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#7A1020]">Ank (शुभांक)</p>
                <p data-testid="lucky-ank-value" className="font-mono text-lg font-extrabold text-[#20050B]">{settings.lucky_ank || "—"}</p>
              </div>
              <div className="px-2 text-center">
                <p className="text-[10px] font-bold uppercase tracking-wide text-[#7A1020]">Final Ank</p>
                <p data-testid="final-ank-value" className="font-mono text-lg font-extrabold text-[#20050B]">{settings.final_ank || "—"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4 py-6">
        <ListingBanner />
        {featured.length > 0 && (
          <section data-testid="home-featured" className="mx-auto mb-6 max-w-[560px]">
            <SectionBanner title="★ Featured Markets ★" variant="red" testid="home-featured-banner" />
            <div className="overflow-hidden rounded-lg border border-[#bdbdbd] bg-white shadow-sm">
              {featured.map((r, i) => (
                <MarketRow key={r.category_id} result={r} index={i} own={r.is_own} onRefresh={refreshOne} guessing={guessing[r.category_id]} streak={passBoard?.streaks?.[r.category_id]} rate={passBoard?.rates?.[r.category_id]} passed={passedToday.has(r.category_id)} onGuessUnlocked={loadGuessing} />
              ))}
            </div>
          </section>
        )}
        {/* LIVE UPDATE — own + public markets, today's revealed result (no previous-day results) */}
        {liveUpdateCards.length > 0 && (
          <section className="mb-6">
            <SectionBanner title="Live Update" variant="red" testid="home-live-banner" />
            <div className="grid gap-3 sm:grid-cols-2">
              {liveUpdateCards.map((m, i) => (
                <Fragment key={m.category_id}>
                <div data-testid={`home-live-${m.category_id}`} className="live-update-card flex flex-col items-center rounded-xl p-3 text-center">
                  <p className="flex items-center justify-center gap-1.5 font-display text-base font-extrabold uppercase italic text-[#5A0A14]">
                    {isMarketActive(m.open_time, m.close_time) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#0B7A3B] px-1.5 py-0.5 text-[8px] font-extrabold uppercase not-italic tracking-wider text-white">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75"></span>
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-200"></span>
                        </span>
                        Live
                      </span>
                    )}
                    {m.category_name}
                  </p>
                  <span className="font-mono text-xl font-extrabold text-[#5A0A14]">{revealedValue(m)}</span>
                </div>
                </Fragment>
              ))}
            </div>
          </section>
        )}

        {/* Result Coming Soon countdown + Aaj Ka Result (own markets) */}
        <section className="mb-8">
          {/* Result Coming Soon — countdown popup */}
          {upcoming.length > 0 && (
            <div data-testid="result-alert-below" className="pop-in mb-4 overflow-hidden rounded-2xl border-2 border-[#7A1020] bg-gradient-to-r from-[#DC2626] via-[#B91C1C] to-[#DC2626] shadow-lg">
              <div className="flex items-center gap-3 px-4 py-3">
                <span className="live-pulse inline-block h-3 w-3 shrink-0 rounded-full bg-white" />
                <div className="flex-1">
                  <p className="text-[11px] font-extrabold uppercase tracking-widest text-white/90">Result Coming Soon</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {upcoming.map((u) => (
                      <span key={u.id} data-testid={`alert-below-${u.id}`} className="text-sm font-extrabold text-white">
                        {u.name}
                        <span className="ml-1 rounded bg-white/20 px-1.5 py-0.5 font-mono text-[11px] font-bold tabular-nums">
                          {u.kind === "open" ? "Open" : "Close"} in {fmtCountdown(u.secs)}
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aaj Ka Result — markets within 5 min of their result time (until complete). Dynamic: own + public. */}
          {shownAaj.length > 0 && (
            <div data-testid="today-result-card" className="gold-panel rounded-2xl p-4 shadow">
              <div data-testid="today-result-head" className="live-fx mb-3 flex items-center justify-center gap-3 rounded-xl px-3 py-2">
                <p className="text-xs font-extrabold uppercase tracking-widest text-white">
                  Today's Result · Coming Soon
                </p>
                <button
                  data-testid="share-all-btn"
                  onClick={() => shareResults("Today's Result", shownAaj.map((r) => ({ name: r.category_name, value: revealedValue(r) })))}
                  className="inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1 text-[11px] font-bold text-white shadow-sm transition-transform hover:scale-105 active:scale-95"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share All
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {shownAaj.map((r) => (
                  <div key={r.category_id} data-testid={`today-result-${r.category_id}`} className="gp-tile flex flex-col items-center rounded-xl px-3 py-3 text-center">
                    {isMarketActive(r.open_time, r.close_time) && (
                      <span data-testid={`live-badge-${r.category_id}`} className="live-blink mb-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white"></span>
                        </span>
                        Live
                      </span>
                    )}
                    <p className="gp-name flex items-center justify-center gap-1.5 font-display text-sm font-extrabold uppercase">
                      {r.category_name}
                    </p>
                    <p className="gp-value font-mono text-2xl font-extrabold tracking-wider sm:text-3xl">{revealedValue(r)}</p>
                    {lingerLeft(r) != null && (
                      <p data-testid={`linger-${r.category_id}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#0B7A3B] px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white">
                        List me · {lingerLeft(r)}s
                      </p>
                    )}
                    {r.open_time && r.close_time && (
                      <p className="mt-0.5 text-[10px] font-bold text-[color:var(--accent-crimson)]">({r.open_time} - {r.close_time})</p>
                    )}
                    <button
                      data-testid={`today-share-${r.category_id}`}
                      onClick={() => shareResult(r.category_name, revealedValue(r), r.open_time && r.close_time ? `(${r.open_time} - ${r.close_time})` : "")}
                      className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#25D366] px-3 py-1 text-[11px] font-bold text-white shadow-sm transition-transform hover:scale-105 active:scale-95"
                    >
                      <Share2 className="h-3.5 w-3.5" /> WhatsApp Share
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <PassBoard data={passBoard} />

        {/* LIVE MATKA RESULT — completed + waiting markets (coming-soon ones move up to Aaj Ka Result) */}
        {publicList.length > 0 && (
          <section className="mx-auto mb-8 max-w-[560px]">
            <SectionBanner title="Live Matka Result (Public Source)" variant="red" testid="home-imported-banner" />
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                data-testid="home-market-search"
                value={marketQuery}
                onChange={(e) => setMarketQuery(e.target.value)}
                placeholder="Search market..."
                className="w-full rounded-xl border border-[color:var(--border-gold)] bg-[color:var(--bg-card)] py-2 pl-9 pr-3 text-sm text-[color:var(--text-primary)] outline-none focus:border-[color:var(--accent-gold)]"
              />
            </div>
            {shownPublic.length > 0 ? (
              <div data-testid="home-imported-list" className="overflow-hidden rounded-lg border border-[#bdbdbd] bg-white shadow-sm">
                {shownPublic.map((r, i) => (
                  <Fragment key={r.category_id}>
                    <MarketRow still result={r} index={i} own={r.is_own} onRefresh={refreshOne} guessing={guessing[r.category_id]} streak={passBoard?.streaks?.[r.category_id]} rate={passBoard?.rates?.[r.category_id]} passed={passedToday.has(r.category_id)} onGuessUnlocked={loadGuessing} />
                  </Fragment>
                ))}
              </div>
            ) : shownAaj.length === 0 ? (
              <p data-testid="home-market-noresult" className="rounded-xl border border-[color:var(--border-gold)] bg-[color:var(--bg-card)] px-4 py-6 text-center text-sm text-[color:var(--text-muted)]">
                No market found for "{marketQuery}"
              </p>
            ) : null}
          </section>
        )}

        {/* Announcements */}
        {anns.length > 0 && (
          <section className="mb-8">
            <div className="mb-3 flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-[color:var(--accent-gold-bright)]" />
              <h2 className="font-display text-lg font-bold text-[color:var(--text-primary)]">Announcements</h2>
            </div>
            <div className="space-y-3">
              {anns.map((a) => (
                <div key={a.id} data-testid={`home-ann-${a.id}`} className={`rounded-xl border p-4 ${a.priority === "high" ? "border-[color:var(--accent-crimson)] bg-[color:var(--accent-crimson)]/10" : "gold-border bg-[color:var(--bg-card)]"}`}>
                  <p className="font-semibold text-[color:var(--text-primary)]">{a.title}</p>
                  {a.body && <p className="mt-1 text-sm text-[color:var(--text-secondary)]">{a.body}</p>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
